import { useRef, useState, useCallback, useEffect } from 'react';
import { Trash2 } from 'lucide-react';
import { useEditorStore } from '../../store/editorStore';
import TextLayerRenderer from './renderers/TextLayerRenderer';
import ImageLayerRenderer from './renderers/ImageLayerRenderer';
import VideoLayerRenderer from './renderers/VideoLayerRenderer';
import ShapeLayerRenderer from './renderers/ShapeLayerRenderer';
import CTALayerRenderer from './renderers/CTALayerRenderer';
import InteractiveLayerRenderer from './renderers/InteractiveLayerRenderer';
import FormLayerRenderer from './renderers/FormLayerRenderer';
import OverlayLayerRenderer from './renderers/OverlayLayerRenderer';
import AudioLayerRenderer from './renderers/AudioLayerRenderer';
import IconLayerRenderer from './renderers/IconLayerRenderer';

// Resize handle positions
const RESIZE_HANDLES = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

export default function CanvasElement({
  layer, canvasWidth, canvasHeight, isSelected, isPreviewMode
}) {
  const selectLayer = useEditorStore((s) => s.selectLayer);
  const updateLayerTransform = useEditorStore((s) => s.updateLayerTransform);
  const setHoveredLayer = useEditorStore((s) => s.setHoveredLayer);
  const setEditingLayer = useEditorStore((s) => s.setEditingLayer);
  const editingLayerId = useEditorStore((s) => s.editingLayerId);
  const deleteLayer = useEditorStore((s) => s.deleteLayer);

  const isDragging = useRef(false);
  const isResizing = useRef(null);
  const dragStart = useRef({ mouseX: 0, mouseY: 0, elemX: 0, elemY: 0 });
  const resizeStart = useRef({ mouseX: 0, mouseY: 0, x: 0, y: 0, width: 0, height: 0 });
  const [localTransform, setLocalTransform] = useState(layer.transform);

  useEffect(() => {
    setLocalTransform(layer.transform);
  }, [layer.transform]);

  const t = localTransform;
  const isEditing = editingLayerId === layer.id;

  // Pixel values for rendering
  const pxX = (t.x / 100) * canvasWidth;
  const pxY = (t.y / 100) * canvasHeight;
  const pxW = (t.width / 100) * canvasWidth;
  const pxH = (t.height / 100) * canvasHeight;

  const containerStyle = {
    position: 'absolute',
    left: pxX,
    top: pxY,
    width: pxW,
    height: pxH,
    transform: `rotate(${t.rotation}deg)`,
    opacity: t.opacity,
    zIndex: t.zIndex,
    cursor: isPreviewMode ? 'default' : layer.locked ? 'not-allowed' : isDragging.current ? 'grabbing' : 'grab',
    userSelect: 'none',
    pointerEvents: isPreviewMode ? 'none' : 'all',
  };

  // ── Drag ──────────────────────────────────────────────────

  const handleMouseDown = useCallback((e) => {
    if (isPreviewMode || layer.locked) return;
    e.stopPropagation();

    selectLayer(layer.id, e.metaKey || e.ctrlKey);

    if (isEditing && layer.data?.type === 'text') return; // let text editor handle

    isDragging.current = true;
    dragStart.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      elemX: t.x,
      elemY: t.y,
    };

    const handleMouseMove = (me) => {
      if (!isDragging.current) return;
      const dx = ((me.clientX - dragStart.current.mouseX) / canvasWidth) * 100;
      const dy = ((me.clientY - dragStart.current.mouseY) / canvasHeight) * 100;
      const newX = Math.max(-50, Math.min(150, dragStart.current.elemX + dx));
      const newY = Math.max(-50, Math.min(150, dragStart.current.elemY + dy));
      setLocalTransform((prev) => ({ ...prev, x: newX, y: newY }));
    };

    const handleMouseUp = () => {
      if (isDragging.current) {
        isDragging.current = false;
        setLocalTransform((prev) => {
          updateLayerTransform(layer.id, { x: prev.x, y: prev.y });
          return prev;
        });
      }
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  }, [isPreviewMode, layer.locked, layer.id, isEditing, layer.data?.type, t.x, t.y, canvasWidth, canvasHeight, selectLayer, updateLayerTransform]);

  // ── Resize ────────────────────────────────────────────────

  const handleResizeMouseDown = useCallback((e, handle) => {
    e.stopPropagation();
    e.preventDefault();
    isResizing.current = handle;
    resizeStart.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      x: t.x, y: t.y, width: t.width, height: t.height,
    };

    const handleMouseMove = (me) => {
      if (!isResizing.current) return;
      const h = isResizing.current;
      const rs = resizeStart.current;
      const dx = ((me.clientX - rs.mouseX) / canvasWidth) * 100;
      const dy = ((me.clientY - rs.mouseY) / canvasHeight) * 100;

      let newX = rs.x, newY = rs.y, newW = rs.width, newH = rs.height;

      if (h.includes('e')) newW = Math.max(5, rs.width + dx);
      if (h.includes('w')) { newX = rs.x + dx; newW = Math.max(5, rs.width - dx); }
      if (h.includes('s')) newH = Math.max(3, rs.height + dy);
      if (h.includes('n')) { newY = rs.y + dy; newH = Math.max(3, rs.height - dy); }

      setLocalTransform((prev) => ({ ...prev, x: newX, y: newY, width: newW, height: newH }));
    };

    const handleMouseUp = () => {
      if (isResizing.current) {
        isResizing.current = null;
        setLocalTransform((prev) => {
          updateLayerTransform(layer.id, { x: prev.x, y: prev.y, width: prev.width, height: prev.height });
          return prev;
        });
      }
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  }, [t, canvasWidth, canvasHeight, layer.id, updateLayerTransform]);

  // ── Double click for text editing ─────────────────────────

  const handleDoubleClick = useCallback((e) => {
    e.stopPropagation();
    if (layer.locked) return;
    if (layer.data?.type === 'text') {
      setEditingLayer(layer.id);
    }
  }, [layer.id, layer.locked, layer.data?.type, setEditingLayer]);

  // ── Render layer content ───────────────────────────────────

  function renderContent() {
    const type = layer.data?.type;
    const props = { layer, isEditing };

    switch (type) {
      case 'text':        return <TextLayerRenderer {...props} />;
      case 'image':       return <ImageLayerRenderer {...props} />;
      case 'video':       return <VideoLayerRenderer {...props} />;
      case 'shape':       return <ShapeLayerRenderer {...props} />;
      case 'cta':         return <CTALayerRenderer {...props} />;
      case 'interactive': return <InteractiveLayerRenderer {...props} />;
      case 'form':        return <FormLayerRenderer {...props} />;
      case 'overlay':     return <OverlayLayerRenderer {...props} />;
      case 'audio':       return <AudioLayerRenderer {...props} />;
      case 'icon':        return <IconLayerRenderer {...props} />;
      default:            return <div className="w-full h-full bg-gray-800/50 border border-dashed border-gray-600" />;
    }
  }

  // Build entrance animation style (preview mode only)
  const anim = layer.animation;
  const animWrapStyle =
    isPreviewMode && anim && anim.preset !== 'none'
      ? {
          width: '100%',
          height: '100%',
          animation: `anim-${anim.preset} ${anim.duration}ms ${anim.easing} ${anim.delay}ms both`,
          perspective: '800px',
        }
      : { width: '100%', height: '100%' };

  return (
    <div
      style={containerStyle}
      onMouseDown={handleMouseDown}
      onMouseEnter={() => !isPreviewMode && setHoveredLayer(layer.id)}
      onMouseLeave={() => !isPreviewMode && setHoveredLayer(null)}
      onDoubleClick={handleDoubleClick}
      className={isSelected && !isPreviewMode ? 'element-selected' : ''}
    >
      {/* Animation wrapper — keeps entrance anim separate from container's rotation */}
      <div style={animWrapStyle}>
        {renderContent()}
      </div>

      {/* Selection handles + delete button */}
      {isSelected && !isPreviewMode && (
        <>
          {RESIZE_HANDLES.map((handle) => (
            <ResizeHandle
              key={handle}
              handle={handle}
              onMouseDown={(e) => handleResizeMouseDown(e, handle)}
            />
          ))}
          {/* Delete button — top-right corner above the element */}
          <button
            onMouseDown={(e) => {
              e.stopPropagation();
              deleteLayer(layer.id);
            }}
            style={{
              position: 'absolute',
              top: -28,
              right: -1,
              width: 24,
              height: 24,
              background: '#ef4444',
              border: '1.5px solid white',
              borderRadius: 6,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              zIndex: 20,
              boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
            }}
            title="Delete layer"
          >
            <Trash2 size={12} color="white" />
          </button>
        </>
      )}
    </div>
  );
}

// ── Resize Handle component ──────────────────────────────────

function ResizeHandle({ handle, onMouseDown }) {
  const posMap = {
    nw: { top: -4, left: -4, cursor: 'nwse-resize' },
    n:  { top: -4, left: '50%', transform: 'translateX(-50%)', cursor: 'ns-resize' },
    ne: { top: -4, right: -4, cursor: 'nesw-resize' },
    e:  { right: -4, top: '50%', transform: 'translateY(-50%)', cursor: 'ew-resize' },
    se: { bottom: -4, right: -4, cursor: 'nwse-resize' },
    s:  { bottom: -4, left: '50%', transform: 'translateX(-50%)', cursor: 'ns-resize' },
    sw: { bottom: -4, left: -4, cursor: 'nesw-resize' },
    w:  { left: -4, top: '50%', transform: 'translateY(-50%)', cursor: 'ew-resize' },
  };

  return (
    <div
      style={{
        position: 'absolute',
        width: 8,
        height: 8,
        background: 'var(--brand-primary)',
        border: '1.5px solid white',
        borderRadius: 2,
        zIndex: 10,
        ...posMap[handle],
      }}
      onMouseDown={(e) => { e.stopPropagation(); onMouseDown(e); }}
    />
  );
}
